"use client"

import { useMemo } from "react"
import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useConnectDeliveryIntegrationMutation } from "@/hooks/mutations/delivery-integrations"
import { useDeliveryProvidersQuery } from "@/hooks/queries/delivery-integrations"
import {
  createDeliveryIntegrationInitialValues,
  ConnectDeliveryIntegrationFormFields,
  normalizeDeliveryIntegrationFormConfig,
  type DeliveryIntegrationFormValues,
} from "@/components/deliveries/delivery-integration-form"
import { useTranslations } from "next-intl"


export function ConnectDeliveryIntegrationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesConnectDeliveryIntegrationDialog")
  const tCommonUi = useTranslations("Common")

  const mutation = useConnectDeliveryIntegrationMutation()
  const providersQuery = useDeliveryProvidersQuery()
  const initialValues = useMemo(
    () => createDeliveryIntegrationInitialValues(),
    [],
  )
  const formik = useFormik<DeliveryIntegrationFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      if (!values.provider) {
        helpers.setFieldError("provider", "Select an integration.")
        return
      }
      try {
        await mutation.mutateAsync({
          ...values,
          provider: values.provider,
          config: normalizeDeliveryIntegrationFormConfig(
            values.provider,
            values.config,
          ),
          setErrors: errors => helpers.setErrors(
            errors as FormikErrors<DeliveryIntegrationFormValues>,
          ),
        })
        helpers.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling has already surfaced the safe server error.
      }
    },
  })

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) formik.resetForm()
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overscroll-contain overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle><span key={tUi("connectADelivery")}>{tUi("connectADelivery")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("chooseADeliveryIntegrationThenEnterItsConnection")}>{tUi("chooseADeliveryIntegrationThenEnterItsConnection")}</span></DialogDescription>
        </DialogHeader>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="min-w-0 space-y-6">
            {providersQuery.error ? (
              <Alert variant="destructive">
                <AlertDescription>
                  <span key={tUi("deliveryIntegrationsCouldNotBeLoadedCloseThis")}>{tUi("deliveryIntegrationsCouldNotBeLoadedCloseThis")}</span></AlertDescription>
              </Alert>
            ) : null}
            <ConnectDeliveryIntegrationFormFields
              providers={providersQuery.data ?? []}
              providersLoading={providersQuery.isLoading}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={formik.isSubmitting}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button
                type="submit"
                disabled={formik.isSubmitting || valuesCannotSubmit(formik.values)}
              >
                <span key={formik.isSubmitting ? tUi("connecting") : tUi("connectTest")}>{formik.isSubmitting ? tUi("connecting") : tUi("connectTest")}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function valuesCannotSubmit(values: DeliveryIntegrationFormValues): boolean {
  return values.provider === ""
}
